import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sparkles, ChevronRight } from "lucide-react";

export function GoalForm({
  goals, selectedGoals, toggleGoal,
  customGoal, setCustomGoal, weight, setWeight,
  weightUnit, setWeightUnit,
  experience, setExperience, loading, handleSubmit, t
}) {
  return (
    <Card className="max-w-2xl mx-auto bg-zinc-950/80 border-zinc-800 backdrop-blur-xl animate-fade-in-up stagger-2" data-testid="goal-form-card">
      <CardContent className="p-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-3">
            <label className="text-sm font-medium text-zinc-300">{t("selectGoals")}</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {goals.map((g) => {
                const isSelected = selectedGoals.includes(g.value);
                let classes = 'bg-zinc-900/50 border-zinc-800 hover:border-zinc-700';
                if (isSelected && g.highlight) classes = 'bg-gradient-to-r from-cyan-500/20 to-violet-500/20 border-cyan-500/50 shadow-lg shadow-cyan-500/10';
                else if (isSelected) classes = 'bg-cyan-500/10 border-cyan-500/50';
                else if (g.highlight) classes = 'bg-gradient-to-r from-cyan-500/5 to-violet-500/5 border-cyan-500/20 hover:border-cyan-500/40';

                return (
                  <button
                    type="button"
                    key={g.value}
                    onClick={() => toggleGoal(g.value)}
                    className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all text-left ${classes}`}
                    data-testid={`goal-${g.value}`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${isSelected ? 'bg-cyan-500 border-cyan-500' : 'border-zinc-600 bg-transparent'}`}>
                      {isSelected && (
                        <svg className="w-3 h-3 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span className="text-lg">{g.icon}</span>
                    <span className={`text-sm font-medium ${isSelected ? 'text-white' : 'text-zinc-300'} ${g.highlight ? 'text-cyan-300' : ''}`}>
                      {g.label}
                    </span>
                    {g.highlight && (
                      <span className="ml-auto text-xs bg-cyan-500/20 text-cyan-400 px-2 py-0.5 rounded-full">{t("popular")}</span>
                    )}
                  </button>
                );
              })}
            </div>

            {selectedGoals.length > 0 && (
              <div className="mt-3 p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                <p className="text-xs text-cyan-400 mb-1">{t("selectGoals").split('(')[0].trim()}:</p>
                <p className="text-sm text-white font-medium">
                  {selectedGoals.map(v => goals.find(g => g.value === v)?.label).join(' + ')}
                </p>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-zinc-300">{t("additionalGoal")}</label>
            <Input
              value={customGoal}
              onChange={(e) => setCustomGoal(e.target.value)}
              placeholder={t("additionalGoalPlaceholder")}
              className="h-12 bg-black/50 border-zinc-800 focus:border-cyan-500"
              data-testid="custom-goal-input"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-zinc-300">
                {t("bodyWeight").replace(/\s*\((kg|lbs)\)\s*$/i, "")} ({weightUnit})
              </label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder={weightUnit === "kg" ? "80" : "175"}
                  className="h-12 bg-black/50 border-zinc-800 focus:border-cyan-500 flex-1"
                  data-testid="weight-input"
                />
                <div
                  className="inline-flex h-12 rounded-md border border-zinc-800 bg-black/50 overflow-hidden"
                  role="tablist"
                  aria-label="Weight unit"
                  data-testid="weight-unit-toggle"
                >
                  <button
                    type="button"
                    onClick={() => setWeightUnit?.("kg")}
                    aria-pressed={weightUnit === "kg"}
                    className={`px-3 text-sm font-semibold transition-colors ${
                      weightUnit === "kg"
                        ? "bg-cyan-500/20 text-cyan-300"
                        : "text-zinc-500 hover:text-zinc-300"
                    }`}
                    data-testid="weight-unit-kg"
                  >
                    kg
                  </button>
                  <button
                    type="button"
                    onClick={() => setWeightUnit?.("lbs")}
                    aria-pressed={weightUnit === "lbs"}
                    className={`px-3 text-sm font-semibold transition-colors ${
                      weightUnit === "lbs"
                        ? "bg-cyan-500/20 text-cyan-300"
                        : "text-zinc-500 hover:text-zinc-300"
                    }`}
                    data-testid="weight-unit-lbs"
                  >
                    lbs
                  </button>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-zinc-300">{t("experienceLevel")}</label>
              <Select value={experience} onValueChange={setExperience} data-testid="experience-select">
                <SelectTrigger className="h-12 bg-black/50 border-zinc-800 focus:border-cyan-500">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800">
                  <SelectItem value="beginner" className="text-white hover:bg-zinc-800">{t("beginner")}</SelectItem>
                  <SelectItem value="intermediate" className="text-white hover:bg-zinc-800">{t("intermediate")}</SelectItem>
                  <SelectItem value="advanced" className="text-white hover:bg-zinc-800">{t("advanced")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full h-14 btn-primary text-lg font-bold rounded-xl"
            disabled={loading}
            data-testid="generate-cycle-btn"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <div className="spinner w-5 h-5" />
                {t("generatingCycle")}
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                {t("generateMyCycle")}
                <ChevronRight className="w-5 h-5" />
              </span>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
